import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/useToast';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import { stripInternalMarkers } from '@/components/ui/editable-table';
import { ASSET_ASSIGNMENTS_QUERY_KEY } from '@/hooks/useAssetAssignments';
import type { Tables } from '@/integrations/supabase/types';
import type {
  AssetWithRelations,
  CreateAssetData,
  UpdateAssetData,
  AssetFilterOptions,
} from '@/types/assets';

/**
 * AUDIT: every `assets` / `asset_attachments` write is audited by the DB
 * triggers (`trg_audit_assets`, `trg_audit_asset_attachments` -> `asset_audit`,
 * docs/AUDIT.md). Never insert `asset_audit` rows from the browser — the
 * table grants `authenticated` SELECT only.
 *
 * PROJECT LINKS: an asset's projects are `project_asset_assignments` rows,
 * written only through the backend (`@/hooks/useAssetAssignments`). The
 * `assigned_project_id` pointer is maintained by those RPCs — never write it
 * here (a DB guard refuses a pointer that disagrees with the open assignment).
 */

/** The per-use values of an asset's OPEN project use (the Things page shows these). */
export type OpenAssetUse = Pick<
  Tables<'project_asset_assignments'>,
  | 'quantity_used'
  | 'unit_code'
  | 'procurement_type_id'
  | 'end_of_life_plan_id'
  | 'waste_treatment_method_id'
  | 'sustainability_notes'
>;

/**
 * An org asset as the list returns it: the asset (whose five per-use columns
 * are its DEFAULTS) + its open assignment — the id (for Return) and that use's
 * per-use values (for the Things page, which edits the current project's use).
 */
export type AssetListItem = AssetWithRelations & {
  /** The asset's open `project_asset_assignments` row, or null when unassigned. */
  open_assignment_id: string | null;
  /** That open use's per-use values, or null when unassigned. */
  open_use: OpenAssetUse | null;
};

/** Every asset write can change what the assignment reads embed (name, code, status…). */
const invalidateAssetCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) => {
  queryClient.invalidateQueries({ queryKey: ['assets', organizationId] });
  queryClient.invalidateQueries({ queryKey: [ASSET_ASSIGNMENTS_QUERY_KEY, organizationId] });
};

/**
 * Fetch all assets for an organization with optional filters. A project's
 * materials are NOT read here — they are its assignment rows
 * (`useProjectAssetUses`), open and closed.
 */
export const useAssets = (
  organizationId?: string,
  options?: AssetFilterOptions
) => {
  return useQuery({
    queryKey: ['assets', organizationId, options],
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }

      // First, get assets with relations
      let query = supabase
        .from('assets')
        .select(`
          *,
          locations(id, name),
          projects(id, name, code),
          businesses(id, name),
          asset_types(id, code, name, icon, color),
          open_assignment:project_asset_assignments!asset_id(
            id, quantity_used, unit_code, procurement_type_id, end_of_life_plan_id,
            waste_treatment_method_id, sustainability_notes
          )
        `)
        .eq('organization_id', organizationId)
        // Filters the EMBED only (no !inner): every asset is still returned,
        // carrying at most one open assignment (one-open-use unique index).
        .is('open_assignment.returned_on', null);

      // Apply filters
      if (options?.status) {
        query = query.eq('status', options.status);
      }
      if (options?.asset_type_id) {
        query = query.eq('asset_type_id', options.asset_type_id);
      }
      if (options?.location_id) {
        query = query.eq('location_id', options.location_id);
      }
      if (options?.business_id) {
        query = query.eq('business_id', options.business_id);
      }

      query = query.order('created_at', { ascending: false });

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Get attachment counts for all assets
      const assetIds = data.map(asset => asset.id);
      const { data: attachmentCounts } = await supabase
        .from('asset_attachments')
        .select('asset_id')
        .in('asset_id', assetIds);

      // Count attachments per asset
      const attachmentCountMap = new Map<string, number>();
      attachmentCounts?.forEach(attachment => {
        const currentCount = attachmentCountMap.get(attachment.asset_id) || 0;
        attachmentCountMap.set(attachment.asset_id, currentCount + 1);
      });

      // Enrich assets with counts and flatten the open-assignment embed
      const enrichedAssets = data.map(({ open_assignment, ...asset }) => {
        const open = open_assignment?.[0] ?? null;
        return {
        ...asset,
        open_assignment_id: open?.id ?? null,
        open_use: open
          ? {
              quantity_used: open.quantity_used,
              unit_code: open.unit_code,
              procurement_type_id: open.procurement_type_id,
              end_of_life_plan_id: open.end_of_life_plan_id,
              waste_treatment_method_id: open.waste_treatment_method_id,
              sustainability_notes: open.sustainability_notes,
            }
          : null,
        attachment_count: attachmentCountMap.get(asset.id) || 0,
        has_expense: !!asset.purchase_expense_id
        };
      });

      return enrichedAssets as unknown as AssetListItem[];
    },
    enabled: !!organizationId,
    // Reference-ish list used by editable grids — refetching on every
    // window focus wipes in-flight cell edits (see EditableTable's INIT
    // effect). 2 min cache balances freshness with data-loss risk.
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Fetch a single asset by ID
 */
export const useAsset = (organizationId?: string, assetId?: string) => {
  return useQuery({
    queryKey: ['asset', organizationId, assetId],
    queryFn: async () => {
      if (!organizationId || !assetId) {
        return null;
      }

      const { data, error } = await supabase
        .from('assets')
        .select(`
          *,
          locations(id, name),
          projects(id, name, code),
          businesses(id, name),
          asset_types(id, code, name, icon, color)
        `)
        .eq('id', assetId)
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        throw error;
      }

      return data as AssetWithRelations;
    },
    enabled: !!organizationId && !!assetId,
  });
};

/**
 * Create a new asset
 */
export const useCreateAsset = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAssetData) => {
      const { data: asset, error } = await supabase
        .from('assets')
        .insert(data)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return asset;
    },
    onSuccess: (data) => {
      invalidateAssetCaches(queryClient, data.organization_id);
      invalidateAuditCaches(queryClient, data.organization_id);
      // Assets linked to expenses feed the expense surfaces' asset_count.
      queryClient.invalidateQueries({ queryKey: ['expenses', data.organization_id] });
      toast.success('Asset created successfully');
    },
    onError: (error: Error) => {
      console.error('Error creating asset:', error);
      toast.error(`Failed to create asset: ${error.message}`);
    },
  });
};

/**
 * Update an existing asset
 */
export const useUpdateAsset = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateAssetData) => {
      const { data: asset, error } = await supabase
        .from('assets')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return asset;
    },
    onSuccess: (data) => {
      invalidateAssetCaches(queryClient, data.organization_id);
      queryClient.invalidateQueries({ queryKey: ['asset', data.organization_id, data.id] });
      invalidateAuditCaches(queryClient, data.organization_id);
      queryClient.invalidateQueries({ queryKey: ['expenses', data.organization_id] });
      toast.success('Asset updated successfully');
    },
    onError: (error: Error) => {
      console.error('Error updating asset:', error);
      toast.error(`Failed to update asset: ${error.message}`);
    },
  });
};

/**
 * Delete an asset
 */
export const useDeleteAsset = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    // The asset's assignments survive the delete: a DB trigger closes the open
    // one as `deleted` and nulls `asset_id` on all of them (the code/name
    // snapshot keeps each project's history readable).
    mutationFn: async ({ id, organizationId }: { id: string; organizationId: string }) => {
      const { error } = await supabase
        .from('assets')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return { id, organizationId };
    },
    onSuccess: (data) => {
      invalidateAssetCaches(queryClient, data.organizationId);
      queryClient.invalidateQueries({ queryKey: ['asset', data.organizationId, data.id] });
      invalidateAuditCaches(queryClient, data.organizationId);
      queryClient.invalidateQueries({ queryKey: ['expenses', data.organizationId] });
      toast.success('Asset deleted successfully');
    },
    onError: (error: Error) => {
      console.error('Error deleting asset:', error);
      toast.error(`Failed to delete asset: ${error.message}`);
    },
  });
};

/**
 * Batch-insert new assets in a single round trip. Used by the materials
 * grid's batch save flow. Audited by the `assets` trigger. Creates carry
 * no project link — the caller assigns them afterwards (`useAssignAssets`).
 */
export const useBatchCreateAssets = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rows: CreateAssetData[]) => {
      if (rows.length === 0) return [];
      const sanitized = rows.map(stripInternalMarkers);
      const { data, error } = await supabase.from('assets').insert(sanitized).select();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const orgId = data[0]?.organization_id;
      if (orgId) {
        invalidateAssetCaches(queryClient, orgId);
      }
    },
    onError: (error: Error) => {
      console.error('Error batch-creating assets:', error);
      toast.error(`Failed to create materials: ${error.message}`);
    },
  });
};

/**
 * Batch-update assets via parallel `.update()` calls. We intentionally
 * do NOT use `.upsert()` — supabase's upsert proposes an INSERT first
 * and only falls back to UPDATE on primary-key conflict, so Postgres
 * validates NOT NULL constraints on the proposed INSERT row. Fields we
 * don't include in the patch (like `organization_id`) would violate
 * NOT NULL during the would-be INSERT even though only the UPDATE path
 * executes. `.update()` skips the INSERT phase entirely, so only the
 * fields we send are touched and omitted fields are left alone. Audited
 * by the `assets` trigger.
 */
export const useBatchUpdateAssets = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rows: Array<Partial<CreateAssetData> & { id: string }>) => {
      if (rows.length === 0) return [];
      const results = await Promise.all(
        rows.map(({ id, ...patch }) =>
          supabase
            .from('assets')
            .update(stripInternalMarkers(patch))
            .eq('id', id)
            .select()
            .single(),
        ),
      );
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;
      return results.map((r) => r.data).filter((d): d is NonNullable<typeof d> => !!d);
    },
    onSuccess: (data) => {
      const orgId = data[0]?.organization_id;
      if (orgId) {
        invalidateAssetCaches(queryClient, orgId);
      }
    },
    onError: (error: Error) => {
      console.error('Error batch-updating assets:', error);
      toast.error(`Failed to update materials: ${error.message}`);
    },
  });
};

/**
 * Link an expense (and optionally a line item) to an asset
 */
export const useLinkExpenseToAsset = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetId,
      organizationId,
      expenseId,
      lineItemId,
    }: {
      assetId: string;
      organizationId: string;
      expenseId: string;
      lineItemId?: string;
      /** Display-only; no longer written (the `assets` trigger audits the link). */
      expenseNumber?: string;
      /** Display-only; no longer written (the `assets` trigger audits the link). */
      lineItemDescription?: string;
    }) => {
      const { data: asset, error } = await supabase
        .from('assets')
        .update({
          purchase_expense_id: expenseId,
          purchase_line_item_id: lineItemId || null,
        })
        .eq('id', assetId)
        .select()
        .single();

      if (error) throw error;

      return { asset, organizationId };
    },
    onSuccess: (data) => {
      invalidateAssetCaches(queryClient, data.organizationId);
      queryClient.invalidateQueries({ queryKey: ['asset', data.organizationId, data.asset.id] });
      invalidateAuditCaches(queryClient, data.organizationId);
      queryClient.invalidateQueries({ queryKey: ['expenses', data.organizationId] });
      toast.success('Expense linked successfully');
    },
    onError: (error: Error) => {
      console.error('Error linking expense:', error);
      toast.error('Failed to link expense');
    },
  });
};

/**
 * Unlink an expense from an asset
 */
export const useUnlinkExpenseFromAsset = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetId,
      organizationId,
    }: {
      assetId: string;
      organizationId: string;
      /** Display-only; no longer written (the `assets` trigger audits the link). */
      expenseNumber?: string;
      /** Display-only; no longer written (the `assets` trigger audits the link). */
      lineItemDescription?: string;
    }) => {
      const { data: asset, error } = await supabase
        .from('assets')
        .update({
          purchase_expense_id: null,
          purchase_line_item_id: null,
        })
        .eq('id', assetId)
        .select()
        .single();

      if (error) throw error;

      return { asset, organizationId };
    },
    onSuccess: (data) => {
      invalidateAssetCaches(queryClient, data.organizationId);
      queryClient.invalidateQueries({ queryKey: ['asset', data.organizationId, data.asset.id] });
      invalidateAuditCaches(queryClient, data.organizationId);
      toast.success('Expense unlinked successfully');
    },
    onError: (error: Error) => {
      console.error('Error unlinking expense:', error);
      toast.error('Failed to unlink expense');
    },
  });
};
